/**
 * ViewModeToggle Component
 *
 * Toggle between code (Monaco) and form (visual) editing modes.
 * Provides a seamless transition between raw JSON and structured forms.
 */

import { Code2, FormInput } from "lucide-react";

import type { ViewMode, ViewModeToggleProps } from "./types";

/**
 * ViewModeToggle - Switch between code and form views
 *
 * CSS Classes:
 * - view-mode-toggle: Container element
 * - view-mode-toggle__button: Individual toggle button
 * - view-mode-toggle__button--active: Active button state
 * - view-mode-toggle__icon: Icon element
 * - view-mode-toggle__label: Button label text
 */
export function ViewModeToggle({
  mode,
  onChange,
  disabled = false,
  className = "",
}: ViewModeToggleProps): React.JSX.Element {
  const handleModeChange = (newMode: ViewMode): void => {
    if (!disabled && newMode !== mode) {
      onChange(newMode);
    }
  };

  return (
    <div
      className={`view-mode-toggle inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 ${className}`}
    >
      <button
        type="button"
        onClick={() => handleModeChange("code")}
        disabled={disabled}
        className={`view-mode-toggle__button flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          mode === "code"
            ? "view-mode-toggle__button--active bg-white text-gray-900 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        aria-pressed={mode === "code"}
      >
        <Code2 className="view-mode-toggle__icon h-4 w-4" />
        <span className="view-mode-toggle__label">Code</span>
      </button>

      <button
        type="button"
        onClick={() => handleModeChange("form")}
        disabled={disabled}
        className={`view-mode-toggle__button flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          mode === "form"
            ? "view-mode-toggle__button--active bg-white text-gray-900 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
        aria-pressed={mode === "form"}
      >
        <FormInput className="view-mode-toggle__icon h-4 w-4" />
        <span className="view-mode-toggle__label">Form</span>
      </button>
    </div>
  );
}

export type { ViewMode, ViewModeToggleProps };
